/* eslint-disable react-native/no-unused-styles */
import { User } from '@akin-home/amplify';
import { Text } from '@akin-home/text/native';
import * as React from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
} from 'react-native';
import styled from 'styled-components/native';
import { dimensions, spacing } from '../../theme';
import { PopUp } from '../PopUp/PopUp';
import { ProfileIcon } from '../ProfileIcon/ProfileIcon';
import { ProfileIcons } from '../ProfileIcons/ProfileIcons';
import { TextList } from '../TextListInput/TextList';
import { TEXT, VIEW } from './DropdownFamily.presets';
import { DropdownFamilyProps } from './DropdownFamily.props';

interface DropDownOptionProps extends TouchableOpacityProps {
  familyMembers: User[];
  preset: DropdownOptionPresetNames;
  title: string;
  selected: boolean;
}

export function DropDownOption(props: DropDownOptionProps): JSX.Element {
  const { familyMembers = [], preset = 'primary', selected, ...rest } = props;
  const fontWeight = selected ? 'bold' : 'normal';

  return (
    <TouchableOpacity {...rest} style={presets[preset]}>
      <Text style={[TEXT, { fontWeight }]}>{props.title}</Text>
      <View style={styles.spacer} />
      <ProfileIcons
        profiles={familyMembers}
        preset="overlapRight"
        displayCount={2}
      />
    </TouchableOpacity>
  );
}

const empty_value: User[] = [];

/**
 * The Dropdown Family display the list of family members in the calendar header
 */
export function DropdownFamily(props: DropdownFamilyProps): JSX.Element {
  const {
    style: styleOverride,
    title = 'People',
    showList = false,
    multiSelect = false,
    familyMembers,
    value = empty_value,
    error,
    onChange,
    ...rest
  } = props;

  let displayTitle = title;

  if (value?.length === 1 && !rest.onlyShowLabel) {
    displayTitle = value[0].name as string;
  }

  let popUp: PopUp | null = null;

  const setPopUpRef = (ref: PopUp) => (popUp = ref);
  const hidePopUp = () => popUp?.hide();
  const showPopUp = () => popUp?.show();

  const onClick = (user: User) => {
    hidePopUp();

    if (multiSelect) {
      if (value.find((current) => current.id === user.id)) {
        onChange?.(value.filter((current) => current.id !== user.id));
      } else {
        onChange?.([...value, user]);
      }
    } else {
      onChange?.([user]);
    }
  };

  return (
    <View style={styles.container}>
      <PopUp
        style={styles.popup}
        animationType="fade"
        transparent={true}
        ref={setPopUpRef}
        anchor={
          <DropdownButton style={styleOverride} {...rest} onPress={showPopUp}>
            <Text style={TEXT}>{displayTitle}</Text>
            {value.length ? (
              <View style={styles.familyMemberIcons}>
                <ProfileIcons
                  profiles={value}
                  preset="overlapRight"
                  displayCount={4}
                />
              </View>
            ) : null}
          </DropdownButton>
        }
      >
        {familyMembers.map((familyMember, index) => (
          <DropDownOption
            key={familyMember.name}
            title={familyMember.name || ''}
            familyMembers={[familyMember]}
            selected={value.includes(familyMember)}
            preset={
              index === 0
                ? 'first'
                : !multiSelect && index + 1 === familyMembers.length
                ? 'last'
                : 'primary'
            }
            onPress={() => onClick(familyMember)}
          />
        ))}

        {props.hideEveryone ? null : (
          <DropDownOption
            key="everyone"
            title="Everyone"
            familyMembers={familyMembers}
            preset="last"
            selected={false}
            onPress={() => {
              hidePopUp();
              onChange && onChange(familyMembers);
            }}
          />
        )}
      </PopUp>
      {error && <Text preset="error">{error.message}</Text>}
      {multiSelect && showList && (
        <TextList
          items={value}
          onPressItem={(_, index) => {
            if (onChange) {
              const newValue = [...value];
              newValue.splice(index, 1);
              onChange(newValue);
            }
          }}
          renderListItem={(user: User) => (
            <>
              <View style={styles.familyMemberNameAndIcon}>
                <Text style={TEXT}>{user.name}</Text>
                <ProfileIcon profile={user} style={styles.familyMemberIcon} />
              </View>
              <View style={styles.spacer} />
            </>
          )}
        />
      )}
    </View>
  );
}

const DropdownButton = styled.TouchableOpacity`
  flex-direction: row;
  justify-content: space-between;
  gap: 20px;
  border-radius: 20px;
  border-color: lightgrey;
  border-width: 2px;
  padding-horizontal: 16px;
  padding-vertical: 12px;
  min-width: 230px;

  background-color: white;
  align-items: center;
`;

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  popup: {
    padding: 0,
    borderRadius: dimensions.buttonRadius,
    borderWidth: 0,
    elevation: 2,
  },
  item: {
    ...VIEW,
    borderRadius: 0,
    borderWidth: 0,
    backgroundColor: 'white',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 8,
    paddingBottom: 8,
  },
  spacer: {
    flex: 1,
    flexBasis: 20,
  },
  familyMemberNameAndIcon: { flexDirection: 'row', alignItems: 'center' },
  familyMemberIcon: {
    marginLeft: spacing[2],
  },
  familyMemberIcons: {
    flexDirection: 'row-reverse',
  },
});

const presets = StyleSheet.create({
  primary: styles.item,
  first: {
    ...styles.item,
    borderTopLeftRadius: dimensions.buttonRadius,
    borderTopRightRadius: dimensions.buttonRadius,
  },
  last: {
    ...styles.item,
    borderBottomLeftRadius: dimensions.buttonRadius,
    borderBottomRightRadius: dimensions.buttonRadius,
  },
});

type DropdownOptionPresetNames = keyof typeof presets;
